import { Button } from '@jutro/legacy/components';
import { useModal } from '@jutro/components';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import { Currency as CurrencyField } from 'gw-components-platform-react';
import { TranslatorContext } from '@jutro/locale';
import _ from 'lodash';
import React, { useEffect, useCallback, useContext, useState } from 'react';
import InstallmentPreviewPopup from './InstallmentPreviewPopupComponent';
import messages from './PaymentPlansComponent.messages';
import metadata from './PaymentPlansComponent.metadata.json5';
import styles from './PaymentPlansComponent.module.scss';

function PaymentPlansComponent(props) {
    // 2025-01-02 - Debra Frum - US25427 - CE - Make a Payment on Offer Details
    // Replaced PaymentContext with page managing state between Payment components
    const { 
        enableEdit,
        offer, bindData, authHeader, 
        baseAmount, setBaseAmount, 
        paymentPlan, setPaymentPlan, 
        setCalcFees
    } = props;

    const [amountToPayValidationMessages, setAmountToPayValidationMessages] = useState([]); 
    const translator = useContext(TranslatorContext);
    const { showModal } = useModal();

    useEffect(() => {
        const initialPaymentAmount = {
            "currency": _.get(offer, 'premium.currency'),
            "amount": _.get(bindData, 'customerPaymentDetails_TWIA.amount')
        }

        setBaseAmount(initialPaymentAmount); 
    }, []);

    const updateSelectedPlan = useCallback(billingId => {
        _.set(bindData, 'selectedPaymentPlan', billingId);
        setPaymentPlan(billingId);
    }, [bindData, setPaymentPlan]);

    const onAmountToPayChange = useCallback(
        amountValue => {
            setBaseAmount(amountValue);
            _.set(bindData, 'customerPaymentDetails_TWIA.amount', _.get(amountValue, 'amount'));
        }, [bindData, setBaseAmount]);

    const onPlanSelect = useCallback(
        selectedValue => {
            const radioValue = _.get(selectedValue, '[0]');
            const selectedPlan = _.get(bindData, `paymentPlans[${radioValue}]`);

            if (selectedPlan) {
                setAmountToPayValidationMessages([]);
                onAmountToPayChange( _.get(selectedPlan, 'downPayment'));
                updateSelectedPlan(selectedPlan.billingId);
            }
        }, 
    [bindData, onAmountToPayChange, updateSelectedPlan]);

    const onInstallments = useCallback(paymentPlan => {
        const componentProps = {
            offer,
            paymentPlan,
            authHeader
        };

        showModal(
            <InstallmentPreviewPopup {...componentProps} />
        ).catch(_.noop);
    }, [offer, authHeader, showModal]);


    const onAmountToPayBlur = useCallback(
        (event, change) => {
            setAmountToPayValidationMessages([]);

            const newAmount = _.get(change, 'value.amount');
            let plans = bindData.paymentPlans;

            if (!plans) {return;}

            // 2024-12-04 - Debra Frum - US25427 - CE - Make a Payment on Offer Details
            // TODO: determine why paymentPlan isn't getting retained or is being overridden
            // When initial value of paymentPlan isn't TWIAFullPay upon entry to page, and then
            //    1. select payment plan TWIAFullPay
            //    2. change Amount to Pay to value < TWIAFullPay downpayment amount
            // paymentPlan is correctly changed to another payment plan, but then somewhere gets set back to 
            // the initial payment plan 
            // const current = plans.find(plan => plan.billingId === paymentPlan); 
            const current = plans.find(plan => plan.billingId === bindData.selectedPaymentPlan); 

            // If Amount to Pay is less than the downpayment of the selected payment method,
            // change payment method to one where the downpayment is met by the Amount to Pay 
            if (newAmount < _.get(current, 'downPayment.amount')) {
                // 2025-01-16 - Debra Frum - US25427, DE17949
                //   Feature Testing_CE-TWIA: Incorrect payment plan change as per the amount entere on Offer Details screen
                // Also include plan where the changed amount is equal to the plan's down-payment amount
                plans = plans.filter(plan => plan.downPayment.amount <= newAmount);
                if (plans && plans.length > 0) {
                    const newPlan = plans.sort(plan => plan.downPayment.amount)[0];

                    // newPlan && updateSelectedPlan(newPlan.billingId);
                    if (newPlan) {
                        updateSelectedPlan(newPlan.billingId);
                    } 
                }
            }

            if (newAmount < bindData.customerPaymentDetails_TWIA.minimumAmount) {
                setAmountToPayValidationMessages([translator(messages.payMinimumAmount)]);
            }
            
            setCalcFees(true)
         }, [bindData.customerPaymentDetails_TWIA.minimumAmount, bindData.paymentPlans,  
             bindData.selectedPaymentPlan, setCalcFees, translator, updateSelectedPlan]);

    const getCurrency = useCallback((item, index, property) => {
       const value = item[property.id];

       return (
           <div className={styles.currencyContainer}>
               <CurrencyField
                   id={`currency_${index}`}
                   value={value}
                   readOnly
                   hideLabel
               />
           </div>
       );
    }, []);

    const getInstallmentsPreview = useCallback((item, index) => (
        <Button id={`update${index}`} onClick={() => onInstallments(item)} disabled={enableEdit}> 
            {translator(messages.viewInstallments)}
        </Button>
    ), [enableEdit, onInstallments, translator]);

    const selectedPlan = () => _.findIndex(bindData.paymentPlans, (plan) => plan.billingId === paymentPlan);

    const isMortgageeEscrowPayMethod = () => _.get(bindData, 'paymentDetails.nonEFTPaymentMethod_TWIA') === 'mortgagee_escrow';

    const overrideProps = {
        mortgageePayMethodMsg: {
            visible: isMortgageeEscrowPayMethod(),
            message: translator(messages.mortgageePayMethodMsg)
        },
        paymentPlansTable: {
            data: bindData.paymentPlans,
            selectedRows: [selectedPlan()],
            selectionType: enableEdit ? "none" : "single",
        },
        amountToPay: {
            validationMessages: amountToPayValidationMessages,
            value: baseAmount,
             readOnly: enableEdit
        },
        validationMessageIfPaymentisMore: {
            content: translator(messages.paymentAmountMoreThanPremium),
            visible: baseAmount?.amount > bindData.paymentPlans[0].total.amount
        }        
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            getCurrency,
            getInstallmentsPreview,
            onPlanSelect,
            onAmountToPayChange,
            onAmountToPayBlur,
        }
    };

    return (
        <MetadataContent
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            {...resolvers}
        />
    );
}

export default PaymentPlansComponent;
